import React from 'react'
import type { PatternType } from '../types'

interface PatternBadgeProps {
  patternType: PatternType
  headline?: string
  confidence?: number
  size?: 'sm' | 'md'
  showConfidence?: boolean
}

const PATTERN_CONFIG: Record<PatternType, { label: string; emoji: string; color: string }> = {
  late_goals: { label: 'Late Goals', emoji: '90+', color: 'var(--blue)' },
  late_concedes: { label: 'Late Concedes', emoji: '90-', color: 'var(--red)' },
  corner_surge: { label: 'High Corners', emoji: 'C+', color: 'var(--green)' },
  corner_drop: { label: 'Low Corners', emoji: 'C-', color: 'var(--muted)' },
  home_away_divergence: { label: 'Venue Split', emoji: 'H/A', color: 'var(--purple)' },
  form_trend_up: { label: 'In Form', emoji: 'UP', color: 'var(--green)' },
  form_trend_down: { label: 'Poor Form', emoji: 'DN', color: 'var(--red)' },
  h2h_dominance: { label: 'H2H Edge', emoji: 'H2H', color: 'var(--pink)' },
  card_prone: { label: 'Card Prone', emoji: 'YC', color: 'var(--yellow)' },
  player_hot_streak: { label: 'Hot Streak', emoji: 'HOT', color: 'var(--orange)' },
  player_cold_streak: { label: 'Cold Streak', emoji: 'CLO', color: 'var(--muted)' },
  player_card_magnet: { label: 'Card Magnet', emoji: 'YC', color: 'var(--yellow)' },
  set_piece_strength: { label: 'Set Piece', emoji: 'SP', color: 'var(--teal)' },
}

export function PatternBadge({
  patternType,
  headline,
  confidence,
  size = 'sm',
  showConfidence = false,
}: PatternBadgeProps) {
  const config = PATTERN_CONFIG[patternType]

  if (!config) {
    return null
  }

  const isSm = size === 'sm'

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isSm ? '0.25rem' : '0.375rem',
        padding: isSm ? '0.125rem 0.375rem' : '0.25rem 0.5rem',
        background: `color-mix(in srgb, ${config.color} 15%, transparent)`,
        border: `1px solid color-mix(in srgb, ${config.color} 40%, transparent)`,
        borderRadius: '4px',
        fontSize: isSm ? '0.625rem' : '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
      title={headline || config.label}
    >
      <span
        style={{
          fontSize: isSm ? '0.5625rem' : '0.6875rem',
          color: config.color,
          fontWeight: 700,
        }}
      >
        {config.emoji}
      </span>
      <span style={{ color: 'var(--fg)' }}>
        {config.label}
      </span>
      {showConfidence && confidence !== undefined && (
        <span
          style={{
            fontSize: isSm ? '0.5625rem' : '0.625rem',
            color: 'var(--muted)',
            opacity: 0.8,
          }}
        >
          {Math.round(confidence * 100)}%
        </span>
      )}
    </div>
  )
}

// Helper to render multiple badges
interface PatternBadgesProps {
  patterns: Array<{ pattern_type: PatternType; headline?: string; confidence_score?: number }>
  limit?: number
  size?: 'sm' | 'md'
}

export function PatternBadges({ patterns, limit = 3, size = 'sm' }: PatternBadgesProps) {
  const visible = patterns.slice(0, limit)
  const remaining = patterns.length - limit

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {visible.map((p, i) => (
        <PatternBadge
          key={i}
          patternType={p.pattern_type}
          headline={p.headline}
          confidence={p.confidence_score}
          size={size}
        />
      ))}
      {remaining > 0 && (
        <span
          style={{
            fontSize: '0.625rem',
            color: 'var(--muted)',
            padding: '0.125rem 0.25rem',
          }}
        >
          +{remaining}
        </span>
      )}
    </div>
  )
}

export default PatternBadge
